import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { DollarSign, Clock, AlertTriangle, CheckCircle2, MessageCircle, ExternalLink, Copy } from "lucide-react";
import { api } from "@/lib/api";

const BUCKETS = [
  { key: "pending",  label: "Pendentes", icon: Clock,          color: "text-amber-700",  bar: "bg-amber-500" },
  { key: "overdue",  label: "Vencidas",  icon: AlertTriangle,  color: "text-red-700",    bar: "bg-red-500" },
  { key: "paid",     label: "Recebidas", icon: CheckCircle2,   color: "text-emerald-700",bar: "bg-emerald-500" },
];

const fmt = (v) => `R$ ${(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function Finance() {
  const [data, setData] = useState({ buckets: { pending: [], overdue: [], paid: [] }, totals: { pending:0, overdue:0, paid:0 }});
  const [loading, setLoading] = useState(true);
  const load = async () => { setLoading(true); const r = await api.get("/finance/summary"); setData(r.data); setLoading(false); };
  useEffect(() => { load(); }, []);

  const remind = async (row) => {
    try {
      const r = await api.post(`/finance/remind/${row.doc_id}`);
      window.open(r.data.wa_url, "_blank");
      toast.success("WhatsApp aberto com a mensagem pronta");
    } catch (e) { toast.error("Falha ao preparar cobrança"); }
  };
  const copyLink = (url) => { navigator.clipboard.writeText(url); toast.success("Link copiado"); };

  return (
    <div className="flex-1 min-w-0 flex flex-col p-3 sm:p-6 lg:p-8 font-sans">
      <div className="flex items-end justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl font-semibold text-slate-900">Painel Financeiro</h1>
          <p className="text-slate-500 mt-1">Honorários cobrados via Stripe — cartão, Pix e boleto.</p>
        </div>
        <button onClick={load} className="text-sm px-4 h-10 rounded-lg border border-slate-200 bg-white hover:bg-slate-50">Atualizar</button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        {BUCKETS.map(b => (
          <div key={b.key} className="bg-white rounded-2xl border border-slate-100 p-6" data-testid={`total-${b.key}`}>
            <div className="flex items-center gap-2">
              <div className={`w-1.5 h-6 rounded-full ${b.bar}`}/>
              <b.icon className={`w-4 h-4 ${b.color}`}/>
              <div className="font-heading font-semibold text-slate-900">{b.label}</div>
            </div>
            <div className="mt-3 text-3xl font-heading font-semibold text-slate-900">{fmt(data.totals[b.key])}</div>
            <div className="text-xs text-slate-500 mt-1">{data.buckets[b.key].length} cobrança(s)</div>
          </div>
        ))}
      </div>

      {loading ? (
        <div className="text-center text-slate-500 py-16">Carregando...</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {BUCKETS.map(b => (
            <section key={b.key} className="bg-slate-100/60 rounded-2xl p-3" data-testid={`col-${b.key}`}>
              <div className="px-2 py-2 flex items-center gap-2">
                <b.icon className={`w-4 h-4 ${b.color}`}/>
                <div className="font-heading font-semibold text-slate-900">{b.label}</div>
                <span className="ml-auto text-xs text-slate-500 tabular-nums">{data.buckets[b.key].length}</span>
              </div>
              <div className="space-y-3">
                {data.buckets[b.key].length === 0 ? (
                  <div className="text-xs text-slate-400 text-center py-6">Vazio</div>
                ) : data.buckets[b.key].map(r => (
                  <div key={r.session_id} className="bg-white rounded-xl border border-slate-100 p-4" data-testid={`row-${r.session_id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <Link to={r.client_id ? `/clients/${r.client_id}` : "#"} className="text-sm font-medium text-slate-900 truncate hover:underline block">{r.client_name || "Cliente"}</Link>
                        <div className="text-xs text-slate-500 mt-0.5">{fmt(r.amount)} · há {r.age_days} dia(s)</div>
                      </div>
                      <Link to={`/documents/${r.doc_id}`} className="text-xs text-[#0f4c5c] hover:underline whitespace-nowrap">Abrir</Link>
                    </div>
                    {b.key !== "paid" && r.payment_link_url && (
                      <div className="flex items-center gap-2 mt-3">
                        <button onClick={() => remind(r)} data-testid={`remind-${r.session_id}`} className="flex-1 h-8 text-xs rounded-lg bg-emerald-600 text-white inline-flex items-center justify-center gap-1 hover:-translate-y-0.5 transition">
                          <MessageCircle className="w-3.5 h-3.5"/> Cobrar
                        </button>
                        <button onClick={() => copyLink(r.payment_link_url)} className="h-8 px-2 text-xs rounded-lg border border-slate-200 hover:bg-slate-50 inline-flex items-center gap-1"><Copy className="w-3.5 h-3.5"/></button>
                        <a href={r.payment_link_url} target="_blank" rel="noreferrer" className="h-8 px-2 text-xs rounded-lg border border-slate-200 hover:bg-slate-50 inline-flex items-center gap-1"><ExternalLink className="w-3.5 h-3.5"/></a>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
      <p className="text-xs text-slate-400 mt-6">Pendentes viram Vencidas após 7 dias sem pagamento. Todo dia às 10h da manhã enviamos um e-mail com botões WhatsApp de 1 clique para as cobranças com +3 dias de atraso.</p>
    </div>
  );
}
